import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Typography,
  Space,
  Divider,
  Tag,
  Button,
  Alert,
  List,
  Avatar,
  Input
} from 'antd';
import '../../styles/font.css';
import { 
  WalletOutlined, 
  GiftOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  PlusOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { dashboardService, DashboardData, IncomeHistoryTransaction, IncomeByGroup, IncomeByGroupResponse } from '../../services/dashboardService';
import { MonthlyPickerModal } from '../../components/common/MonthlyPickerModal';
import styles from './DashboardPageMobile.module.css';

const { Title, Text } = Typography;

const DashboardPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [currentMonth, setCurrentMonth] = useState(dayjs().format('MM/YYYY'));
  const [showMonthlyPicker, setShowMonthlyPicker] = useState(false);
  
  // State for dashboard data with default values
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalExpenses: 0,
    totalIncome: 0,
    totalGifts: 0,
    monthlyExpenses: 0,
    monthlyIncome: 0,
    monthlyGifts: 0,
    topExpenseCategories: [],
    recentTransactions: []
  });

  // State for income by group
  const [incomeByGroup, setIncomeByGroup] = useState<any[]>([]);
  const [incomeByGroupLoading, setIncomeByGroupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setError(null);
        const monthParam = selectedMonth.format('YYYY-MM');
        const data = await dashboardService.getDashboardData({ month: monthParam });
        setDashboardData(data);
        setCurrentMonth(selectedMonth.format('MM/YYYY'));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Không thể tải dữ liệu dashboard. Vui lòng thử lại sau.');
      }
    };

    fetchDashboardData();
  }, [selectedMonth]);

  // Fetch income by group
  useEffect(() => {
    const fetchIncomeByGroup = async () => {
      try {
        setIncomeByGroupLoading(true);
        const monthParam = selectedMonth.format('YYYY-MM');
        const data: IncomeByGroupResponse = await dashboardService.getIncomeByGroup({ month: monthParam });
        setIncomeByGroup(data.incomeByGroup);
      } catch (err) {
        console.error('Error fetching income by group:', err);
      } finally {
        setIncomeByGroupLoading(false);
      }
    };

    fetchIncomeByGroup();
  }, [selectedMonth]);

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
    // Thêm khoảng trắng trước ký hiệu ₫
    return formatted.replace('₫', ' ₫');
  };

  const getBalance = () => {
    return dashboardData.totalIncome + dashboardData.totalGifts - dashboardData.totalExpenses;
  };

  const getMonthlyBalance = () => {
    return dashboardData.monthlyIncome + dashboardData.monthlyGifts - dashboardData.monthlyExpenses;
  };

  const handleMonthSelect = (date: dayjs.Dayjs) => {
    setSelectedMonth(date);
    setShowMonthlyPicker(false);
  };

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Alert
            message="Lỗi"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                Thử lại
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Main Content */}
      <div className={styles.content}>
        {/* Balance Card */}
        <Card className={styles.balanceCard}>
          <div className={styles.balanceHeader}>
            <div className={styles.balanceIcon}>
              <DollarOutlined />
            </div>
            <div className={styles.balanceInfo}>
              <Text className={styles.balanceLabel}>Số dư hiện tại</Text>
              <Title level={3} className={styles.balanceAmount}>
                {formatCurrency(getBalance())}
              </Title>
            </div>
          </div>
          <div className={styles.balanceDetails}>
            <div className={styles.balanceItem}>
              <ArrowUpOutlined className={styles.incomeIcon} />
              <div>
                <div><Text className={styles.balanceItemLabel}>Thu nhập</Text></div>
                <Text className={styles.balanceItemAmount}>
                  {formatCurrency(dashboardData.totalIncome)}
                </Text>
              </div>
            </div>
            <div className={styles.balanceItem}>
              <GiftOutlined className={styles.giftIcon} />
              <div>
                <div><Text className={styles.balanceItemLabel}>Quà tặng</Text></div>
                <Text className={styles.balanceItemAmount}>
                  {formatCurrency(dashboardData.totalGifts)}
                </Text>
              </div>
            </div>
            <div className={styles.balanceItem}>
              <ArrowDownOutlined className={styles.expenseIcon} />
              <div>
                <div><Text className={styles.balanceItemLabel}>Chi tiêu</Text></div>
                <Text className={styles.balanceItemAmount}>
                  {formatCurrency(dashboardData.totalExpenses)}
                </Text>
              </div>
            </div>
          </div>
        </Card>

        {/* Monthly Overview */}
        <div className={styles.monthlySection}>
          <div className={styles.monthlyHeader}>
            <div className={styles.titleWithPicker}>
              <Title level={4} className={styles.sectionTitle}>
                Tháng
              </Title>
              <div className={styles.monthInputWrapper}>
                <Input
                  className={styles.monthInput}
                  placeholder="Chọn tháng"
                  value={selectedMonth.format('MM/YYYY')}
                  readOnly
                  onClick={() => setShowMonthlyPicker(true)}
                />
              </div>
            </div>
          </div>
          
          <Row gutter={[12, 12]} className={styles.monthlyCards}>
            <Col span={12}>
              <Card className={styles.monthlyCard}>
                <div className={styles.monthlyCardContent}>
                  <div className={`${styles.monthlyIcon} ${styles.income}`}>
                    <RiseOutlined />
                  </div>
                  <div className={styles.monthlyInfo}>
                    <Text className={styles.monthlyLabel}>Thu nhập</Text>
                    <Text className={styles.monthlyAmount}>
                      {formatCurrency(dashboardData.monthlyIncome + dashboardData.monthlyGifts)}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card className={styles.monthlyCard}>
                <div className={styles.monthlyCardContent}>
                  <div className={`${styles.monthlyIcon} ${styles.expense}`}>
                    <FallOutlined />
                  </div>
                  <div className={styles.monthlyInfo}>
                    <Text className={styles.monthlyLabel}>Chi tiêu</Text>
                    <Text className={styles.monthlyAmount}>
                      {formatCurrency(dashboardData.monthlyExpenses)}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Monthly Balance */}
          <Card className={styles.monthlyBalanceCard}>
            <div className={styles.monthlyBalanceContent}>
              <Text className={styles.monthlyBalanceLabel}>
                Số dư tháng {currentMonth}
              </Text>
              <Title level={3} className={styles.monthlyBalanceAmount}>
                {formatCurrency(getMonthlyBalance())}
              </Title>
              <Progress 
                percent={Math.round((getMonthlyBalance() / (dashboardData.monthlyIncome + dashboardData.monthlyGifts)) * 100)}
                strokeColor="#f07a8ef1 "
                showInfo={false}
                className={styles.monthlyProgress}
              />
            </div>
          </Card>
        </div>

        {/* Income by Group Section */}
        {incomeByGroup.length > 0 && (
          <div className={styles.incomeByGroupSection}>
            <div className={styles.sectionHeader}>
              <Title level={4} className={styles.sectionTitle}>
                Thu nhập theo danh mục
              </Title>
              <Button 
                type="link" 
                size="small"
                onClick={() => navigate('/income')}
                className={styles.viewAllButton}
              >
                Xem tất cả
              </Button>
            </div>
            
            <Card className={styles.incomeByGroupCard}>
              {incomeByGroup.map((group, index) => (
                <div key={index} className={styles.groupItem}>
                  <div className={styles.groupInfo}>
                    <div className={styles.groupHeader}>
                      <TeamOutlined className={styles.groupIcon} />
                      <Text className={styles.groupName}>{group.group_name}</Text>
                    </div>
                    <Text className={styles.groupAmount}>
                      {formatCurrency(group.amount)}
                    </Text>
                  </div>
                  <div className={styles.groupProgress}>
                    <Progress 
                      percent={group.percentage} 
                      strokeColor="#52c41a"
                      showInfo={false}
                      size="small"
                    />
                    <Text className={styles.groupPercentage}>
                      {group.percentage}%
                    </Text>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Top Expense Categories */}
        {dashboardData.topExpenseCategories.length > 0 && (
          <div className={styles.categoriesSection}>
            <div className={styles.sectionHeader}>
              <Title level={4} className={styles.sectionTitle}>
                Chi tiêu theo danh mục
              </Title>
              <Button 
                type="link" 
                size="small"
                onClick={() => navigate('/expenses')}
                className={styles.viewAllButton}
              >
                Xem tất cả
              </Button>
            </div>
            
            <Card className={styles.categoriesCard}>
              {dashboardData.topExpenseCategories.map((category, index) => (
                <div key={index} className={styles.categoryItem}>
                  <div className={styles.categoryInfo}>
                    <div className={styles.categoryHeader}>
                      <Text className={styles.categoryName}>{category.name}</Text>
                    </div>
                    <Text className={styles.categoryAmount}>
                      {formatCurrency(category.amount)}
                    </Text>
                  </div>
                  <div className={styles.categoryProgress}>
                    <Progress 
                      percent={category.percentage} 
                      strokeColor="#ff89a1"
                      showInfo={false}
                      size="small"
                    />
                    <Text className={styles.categoryPercentage}>
                      {category.percentage}%
                    </Text>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Recent Transactions */}
        {dashboardData.recentTransactions.length > 0 && (
          <div className={styles.transactionsSection}>
            <div className={styles.sectionHeader}>
              <Title level={4} className={styles.sectionTitle}>
                Giao dịch gần đây
              </Title>
              <Button 
                type="link" 
                size="small"
                onClick={() => navigate('/transactions')}
                className={styles.viewAllButton}
              >
                Xem tất cả
              </Button>
            </div>
            
            <Card className={styles.transactionsCard}>
              {dashboardData.recentTransactions.map((transaction, index) => (
                <div key={index} className={styles.transactionItem}>
                  <div className={styles.transactionIcon}>
                    {transaction.type === 'expense' && <ArrowDownOutlined className={styles.expenseIcon} />}
                    {transaction.type === 'income' && <ArrowUpOutlined className={styles.incomeIcon} />}
                    {transaction.type === 'gift' && <GiftOutlined className={styles.giftIcon} />}
                  </div>
                  <div className={styles.transactionInfo}>
                    <Text className={styles.transactionDescription}>
                      {transaction.description}  <></>  
                    </Text>
                    <Text className={styles.transactionDate}>
                      {dayjs(transaction.date).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                  <div className={styles.transactionAmount}>
                    <Text className={`${styles.amount} ${transaction.type === 'expense' ? styles.expense : styles.income}`}>
                      {transaction.type === 'expense' ? '- ' : '+ '}
                      {formatCurrency(transaction.amount)}
                    </Text>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

      {/* Monthly Picker Modal */}
      <MonthlyPickerModal
        isOpen={showMonthlyPicker}
        onClose={() => setShowMonthlyPicker(false)}
        onSelect={handleMonthSelect}
        selectedDate={selectedMonth}
        title="Chọn tháng"
      />
    </div>
  );
};

export default DashboardPageMobile;
